import { useState } from 'react';
import { Link, createFileRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ApiKey, Mode } from '@tabservice/tabfile-client';
import { ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, LabelValue, LabelValueList, SectionHeader } from '@/components/ui/card';
import { ConfirmDialog } from '@/components/ui/confirm';
import { Dialog, DialogClose, DialogContent } from '@/components/ui/dialog';
import { DetailSkeleton, ErrorNote, Spinner, TableSkeleton } from '@/components/ui/feedback';
import { Pill } from '@/components/ui/pill';
import { Table, TablePagination, TableWrap, TBody, TD, TH, THead, TR } from '@/components/ui/table';
import { useToast } from '@/components/ui/toast';
import { Checkbox } from '@/components/ui/toggles';
import { Field, Input } from '@/components/ui/field';
import { useSetPageTitle } from '@/components/page-title';
import { adminCustomerQuery, formsQuery, queryKeys, adminCustomerKeysPageQuery, adminCustomerUsersPageQuery } from '@/lib/queries';
import { modeCopy } from '@/lib/mode';
import { tabfile, errorMessage } from '@/lib/api';
import { formatCount, formatDate, formatMonth, formatRelative } from '@/lib/format';

/** Rows per page, matching the portal's tables. */
const PAGE_SIZE = 10;

/**
 * The "Tab Service portal client" row stays hidden until the portal reads the link. Today
 * the portal decides who sees E-Filing by email alone, so a site connected here changes
 * nothing for anyone, and staff were reading the row as if it granted access. The API
 * field and the stored value are untouched; turn this on once the portal uses them.
 */
const SHOW_PORTAL_CLIENT = false;

/**
 * A portal site id is a whole positive number, or nothing at all - nothing means this
 * customer is not a portal client, which is a real answer rather than an empty field.
 */
function portalSiteValid(draft: string | null): boolean {
  const trimmed = (draft ?? '').trim();
  if (trimmed === '') return true;
  return /^\d+$/.test(trimmed) && Number(trimmed) > 0;
}

export const Route = createFileRoute('/_app/admin/customers/$customerId')({
  // The shape this page takes, rather than a search box over a table for
  // every route: the screen must not rearrange itself once the data lands.
  pendingComponent: () => <DetailSkeleton />,
  loader: ({ context, params }) => context.queryClient.ensureQueryData(adminCustomerQuery(params.customerId)),
  component: AdminCustomerPage,
});

function AdminCustomerPage() {
  const { customerId } = Route.useParams();
  const queryClient = useQueryClient();
  const toast = useToast();
  const customer = useQuery(adminCustomerQuery(customerId));
  // Keys and users used to arrive inside the customer, whole. A customer with three
  // hundred users sent three hundred to draw ten rows, with no way to ask for the rest.
  const [keysPage, setKeysPage] = useState(1);
  const [usersPage, setUsersPage] = useState(1);
  const keys = useQuery(adminCustomerKeysPageQuery(customerId, keysPage, PAGE_SIZE));
  const users = useQuery(adminCustomerUsersPageQuery(customerId, usersPage, PAGE_SIZE));

  const [modeTarget, setModeTarget] = useState<Mode | null>(null);
  /** The suspend or reactivate being confirmed, or null when neither is. */
  const [statusTarget, setStatusTarget] = useState<'active' | 'suspended' | null>(null);
  const [keyToRevoke, setKeyToRevoke] = useState<ApiKey | null>(null);
  /** The forms being edited, or null while the dialog is closed. */
  const [formsDraft, setFormsDraft] = useState<string[] | null>(null);
  /** The portal site being typed in, or null when the dialog is shut. '' means disconnect. */
  const [portalSiteDraft, setPortalSiteDraft] = useState<string | null>(null);
  const forms = useQuery(formsQuery());

  const data = customer.data;
  useSetPageTitle(data?.name ?? null);

  /**
   * Which forms this customer may file. Adding one opens that form's uploads, checks and
   * e-files to them at once; removing one refuses new e-files of that form, and leaves the
   * e-files they already sent exactly as they are.
   */
  const setForms = useMutation({
    mutationFn: (enabledForms: string[]) =>
      tabfile.PATCH('/v1/admin/customers/{customerId}', {
        params: { path: { customerId } },
        body: { enabledForms },
      }),
    onSuccess: (updated) => {
      toast.success(
        `${updated.name} can file ${updated.enabledForms.length} form${updated.enabledForms.length === 1 ? '' : 's'}`,
        updated.enabledForms.join(', '),
      );
      setFormsDraft(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomer(customerId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomers });
    },
    onError: (error) => toast.error('Could not change the forms', errorMessage(error)),
  });

  /**
   * Which portal client this customer is, if any.
   *
   * Connecting one is what puts E-Filing in that client's portal menu and lets their people
   * cross over without signing in again. Disconnecting takes both away and leaves every
   * e-file they have already sent exactly as it is.
   */
  const setPortalSite = useMutation({
    mutationFn: (portalSiteId: number | null) =>
      tabfile.PATCH('/v1/admin/customers/{customerId}', {
        params: { path: { customerId } },
        body: { portalSiteId },
      }),
    onSuccess: (updated) => {
      toast.success(
        updated.portalSiteId == null
          ? `${updated.name} is no longer a portal client`
          : `${updated.name} is portal site ${updated.portalSiteId}`,
        updated.portalSiteId == null
          ? 'Their people can still sign in to e-filing directly.'
          : 'Their people will see E-Filing in the portal menu.',
      );
      setPortalSiteDraft(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomer(customerId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomers });
    },
    onError: (error) => toast.error('Could not change the portal client', errorMessage(error)),
  });

  const switchMode = useMutation({
    mutationFn: (mode: Mode) =>
      tabfile.PATCH('/v1/admin/customers/{customerId}', { params: { path: { customerId } }, body: { mode } }),
    onSuccess: (updated) => {
      toast.success(
        `${updated.name} is now in ${modeCopy[updated.mode].label.toLowerCase()} mode`,
        modeCopy[updated.mode].hint,
      );
      setModeTarget(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomer(customerId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomers });
    },
    onError: (error) => toast.error('Could not change the mode', errorMessage(error)),
  });

  /**
   * Suspending is how Tab Service cuts a customer off. It is not a delete: the customer, its
   * users, its keys and every e-file it has filed stay exactly as they are, and reactivating
   * puts it all back. What changes is that nothing of theirs is accepted any more - caller.ts
   * refuses a suspended customer whether the request arrives with a session or an API key.
   *
   * Work already on its way to the IRS is deliberately left to finish. A transmission the IRS
   * has accepted, whose acknowledgement we then stopped collecting, leaves that customer's
   * records in a state nobody can read - which is worse than the few minutes it takes to
   * land.
   */
  const setStatus = useMutation({
    mutationFn: (status: 'active' | 'suspended') =>
      tabfile.PATCH('/v1/admin/customers/{customerId}', {
        params: { path: { customerId } },
        body: { status },
      }),
    onSuccess: (updated) => {
      toast.success(
        updated.status === 'suspended' ? `${updated.name} is suspended` : `${updated.name} is active again`,
        updated.status === 'suspended'
          ? 'Nobody on this account can sign in or file. E-files already sent to the IRS will still finish.'
          : 'Everyone on this account can sign in and file again.',
      );
      setStatusTarget(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomer(customerId) });
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomers });
    },
    onError: (error) => toast.error('Could not change the status', errorMessage(error)),
  });

  const revokeKey = useMutation({
    mutationFn: (keyId: string) =>
      tabfile.DELETE('/v1/admin/customers/{customerId}/keys/{keyId}', {
        params: { path: { customerId, keyId } },
      }),
    onSuccess: (_result, keyId) => {
      const key = keys.data?.items?.find((item) => item.id === keyId);
      toast.success(`Key ${key?.prefix ?? ''} revoked`, 'Requests with it are refused from now on.');
      setKeyToRevoke(null);
      void queryClient.invalidateQueries({ queryKey: queryKeys.adminCustomer(customerId) });
    },
    onError: (error) => toast.error('Could not revoke the key', errorMessage(error)),
  });

  if (customer.isError) {
    return (
      <Card>
        <ErrorNote title="Could not load this customer" detail={errorMessage(customer.error)} />
      </Card>
    );
  }

  const live = data?.mode === 'live';

  const suspended = data?.status === 'suspended';

  return (
    <>
      <div>
        <Button asChild variant="ghost" size="sm" className="-ml-3">
          <Link to="/admin">
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            All customers
          </Link>
        </Button>
      </div>

      <Card data-tour="admin-account">
        <SectionHeader
          title="Account"
          description={data ? modeCopy[data.mode].hint : 'Loading the current mode.'}
          actions={
            <div className="flex flex-wrap items-center gap-3">
              <Button
                variant={live ? 'outline' : 'primary'}
                disabled={!data || suspended}
                onClick={() => setModeTarget(live ? 'ats' : 'live')}
              >
                {live ? 'Move back to IRS test mode' : 'Switch to live e-file'}
              </Button>
              {/*
                Reactivating is ordinary, so it is an ordinary button. Suspending stops a
                customer working, so it looks like what it is.
              */}
              <Button
                variant={suspended ? 'primary' : 'outline'}
                disabled={!data}
                onClick={() => setStatusTarget(suspended ? 'active' : 'suspended')}
              >
                {suspended ? 'Reactivate customer' : 'Suspend customer'}
              </Button>
            </div>
          }
        />
        {data ? (
          <LabelValueList>
            <LabelValue label="E-file mode">
              <Pill tone={modeCopy[data.mode].tone}>{modeCopy[data.mode].label}</Pill>
            </LabelValue>
            <LabelValue label="Status">
              <Pill tone={data.status === 'active' ? 'success' : 'warning'}>
                {data.status === 'active' ? 'Active' : 'Suspended'}
              </Pill>
            </LabelValue>
            <LabelValue label="Forms they can file">
              <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                <span>{data.enabledForms.length > 0 ? data.enabledForms.join(', ') : 'None'}</span>
                {/* Outlined like Suspend customer: as plain text it read as part of the form list. */}
                <Button variant="outline" size="sm" onClick={() => setFormsDraft(data.enabledForms)}>
                  Edit forms
                </Button>
              </span>
            </LabelValue>
            {SHOW_PORTAL_CLIENT ? (
              <LabelValue label="Tab Service portal client">
                <span className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <span>
                    {data.portalSiteId === null || data.portalSiteId === undefined
                      ? 'Not connected'
                      : `Site ${data.portalSiteId}`}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setPortalSiteDraft(data.portalSiteId == null ? '' : String(data.portalSiteId))}
                  >
                    {data.portalSiteId == null ? 'Connect' : 'Change'}
                  </Button>
                </span>
              </LabelValue>
            ) : null}
            <LabelValue label="Created">{formatDate(data.createdAt)}</LabelValue>
          </LabelValueList>
        ) : (
          <TableSkeleton rows={4} columns={2} />
        )}
      </Card>

      <Card data-tour="admin-keys">
        <SectionHeader
          title="API keys"
          description="Keys this customer files with. Their owner creates keys from Settings; Tab Service can only revoke them."
        />
        {customer.isPending ? (
          <TableSkeleton rows={2} columns={4} />
        ) : (
          <>
            <TableWrap>
              <Table>
                <THead>
                  <tr>
                    <TH>Name</TH>
                    <TH>Prefix</TH>
                    <TH>Mode</TH>
                    <TH>Status</TH>
                    <TH>Last used</TH>
                    <TH className="text-right">
                      <span className="sr-only">Actions</span>
                    </TH>
                  </tr>
                </THead>
                <TBody>
                  {(keys.data?.items ?? []).length === 0 ? (
                    <TR>
                      <TD colSpan={6} className="text-muted-foreground">
                        No keys yet. The owner creates them after signing in.
                      </TD>
                    </TR>
                  ) : null}
                  {(keys.data?.items ?? []).map((key) => (
                    <TR key={key.id}>
                      <TD className="font-medium">{key.name}</TD>
                      <TD className="text-muted-foreground">{key.prefix}</TD>
                      <TD>
                        <Pill tone={key.mode === 'live' ? 'warning' : 'info'}>
                          {key.mode === 'live' ? 'Live' : 'IRS test'}
                        </Pill>
                      </TD>
                      <TD>
                        <Pill tone={key.status === 'active' ? 'success' : 'neutral'}>
                          {key.status === 'active' ? 'Active' : 'Revoked'}
                        </Pill>
                      </TD>
                      <TD className="text-muted-foreground">
                        {key.lastUsedAt ? formatRelative(key.lastUsedAt) : 'never'}
                      </TD>
                      <TD className="text-right">
                        {key.status === 'active' ? (
                          <Button variant="ghost" size="sm" onClick={() => setKeyToRevoke(key)}>
                            Revoke
                          </Button>
                        ) : null}
                      </TD>
                    </TR>
                  ))}
                </TBody>
              </Table>
            </TableWrap>
            <TablePagination
              page={keysPage}
              pageSize={PAGE_SIZE}
              total={keys.data?.total ?? keys.data?.items?.length ?? 0}
              busy={keys.isFetching}
              onPageChange={setKeysPage}
            />
          </>
        )}
      </Card>

      <Card data-tour="admin-users">
        <SectionHeader title="Users" description="Who can sign in for this customer." />
        <>
          <TableWrap>
            <Table>
              <THead>
                <tr>
                  <TH>Email</TH>
                  <TH>Role</TH>
                  <TH>Status</TH>
                </tr>
              </THead>
              <TBody>
                {(users.data?.items ?? []).map((user) => (
                  <TR key={user.id}>
                    <TD className="font-medium">{user.email}</TD>
                    <TD className="text-muted-foreground">{user.role}</TD>
                    <TD>
                      <Pill tone={user.status === 'active' ? 'success' : user.status === 'invited' ? 'info' : 'neutral'}>
                        {user.status}
                      </Pill>
                    </TD>
                  </TR>
                ))}
              </TBody>
            </Table>
          </TableWrap>
          <TablePagination
            page={usersPage}
            pageSize={PAGE_SIZE}
            total={users.data?.total ?? users.data?.items?.length ?? 0}
            busy={users.isFetching}
            onPageChange={setUsersPage}
          />
        </>
      </Card>

      <Card data-tour="admin-customer-usage">
        <SectionHeader title="Usage" description="Recent months." />
        <TableWrap>
          <Table>
            <THead>
              <tr>
                <TH>Month</TH>
                <TH align="right">Records checked</TH>
                <TH align="right">Records filed</TH>
                <TH align="right">Transmissions</TH>
              </tr>
            </THead>
            <TBody>
              {(data?.usage ?? []).map((row) => (
                <TR key={row.month}>
                  <TD>{formatMonth(row.month)}</TD>
                  <TD numeric>{formatCount(row.recordsChecked)}</TD>
                  <TD numeric>{formatCount(row.recordsFiled)}</TD>
                  <TD numeric>{formatCount(row.transmissions)}</TD>
                </TR>
              ))}
            </TBody>
          </Table>
        </TableWrap>
      </Card>

      <Dialog
        open={portalSiteDraft !== null}
        onOpenChange={(open) => (open ? null : setPortalSiteDraft(null))}
      >
        <DialogContent
          title="Tab Service portal client"
          description="The portal site this customer is. Connect one and their people see E-Filing in the portal menu and cross over without signing in again. Leave it empty to disconnect them; everything they have already filed is untouched."
          footer={
            <>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                variant="primary"
                disabled={setPortalSite.isPending || !portalSiteValid(portalSiteDraft)}
                onClick={() => {
                  const trimmed = (portalSiteDraft ?? '').trim();
                  setPortalSite.mutate(trimmed === '' ? null : Number(trimmed));
                }}
              >
                {setPortalSite.isPending ? <Spinner /> : null}
                {(portalSiteDraft ?? '').trim() === '' ? 'Disconnect' : 'Save'}
              </Button>
            </>
          }
        >
          <Field
            label="Portal site id"
            hint="The site id from the Tab Service portal. Empty means this customer is not a portal client."
            error={portalSiteValid(portalSiteDraft) ? undefined : 'Enter a whole number, or leave it empty.'}
          >
            {(props) => (
              <Input
                {...props}
                value={portalSiteDraft ?? ''}
                inputMode="numeric"
                autoFocus
                placeholder="e.g. 20014"
                onChange={(event) => setPortalSiteDraft(event.target.value)}
              />
            )}
          </Field>
        </DialogContent>
      </Dialog>

      <Dialog open={formsDraft !== null} onOpenChange={(open) => (open ? null : setFormsDraft(null))}>
        <DialogContent
          title="Forms they can file"
          description="Tick a form to let this customer upload, check and e-file it. Untick one and new e-files of that form are refused; what they already filed is untouched."
          footer={
            <>
              <DialogClose asChild>
                <Button variant="outline">Cancel</Button>
              </DialogClose>
              <Button
                variant="primary"
                disabled={!formsDraft || formsDraft.length === 0 || setForms.isPending}
                onClick={() => {
                  if (formsDraft) setForms.mutate(formsDraft);
                }}
              >
                {setForms.isPending ? <Spinner /> : null}
                Save forms
              </Button>
            </>
          }
        >
          <fieldset className="flex flex-col gap-2">
            <legend className="sr-only">Forms they can file</legend>
            {formsDraft && formsDraft.length === 0 ? (
              <p className="mb-2 text-base text-destructive">A customer needs at least one form to file.</p>
            ) : null}
            <div className="grid grid-cols-2 gap-2">
              {(forms.data?.forms ?? []).map((form) => {
                const checked = formsDraft?.includes(form.formType) ?? false;
                return (
                  <label
                    key={form.formType}
                    className="flex cursor-pointer items-center gap-2.5 rounded-md px-1.5 py-1 transition-colors duration-150 ease-out hover:bg-[color:var(--ts-row-hover)]"
                  >
                    <Checkbox
                      checked={checked}
                      label={form.formType}
                      onCheckedChange={(next) =>
                        setFormsDraft((current) => {
                          const list = current ?? [];
                          return next ? [...list, form.formType] : list.filter((value) => value !== form.formType);
                        })
                      }
                    />
                    <span className="text-base text-gray-3">{form.formType}</span>
                  </label>
                );
              })}
            </div>
          </fieldset>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        open={statusTarget !== null}
        onOpenChange={(open) => (open ? null : setStatusTarget(null))}
        title={`${statusTarget === 'suspended' ? 'Suspend' : 'Reactivate'} ${data?.name ?? 'customer'}`}
        description={
          statusTarget === 'suspended' ? (
            <>
              Nobody on this account will be able to sign in or file, and their API keys will be
              refused. Nothing is deleted: their users, keys and e-files stay as they are, and
              reactivating puts it all back.
              <br />
              <br />
              E-files already on their way to the IRS will finish, so their acknowledgements are
              still recorded.
            </>
          ) : (
            <>Everyone on this account will be able to sign in and file again, with the keys and
            settings they had before.</>
          )
        }
        confirmLabel={statusTarget === 'suspended' ? 'Suspend' : 'Reactivate'}
        destructive={statusTarget === 'suspended'}
        requireTyped={statusTarget === 'suspended' ? 'SUSPEND' : undefined}
        pending={setStatus.isPending}
        onConfirm={() => {
          if (statusTarget) setStatus.mutate(statusTarget);
        }}
      />

      <ConfirmDialog
        open={modeTarget !== null}
        onOpenChange={(open) => {
          if (!open) setModeTarget(null);
        }}
        title={modeTarget === 'live' ? 'Switch this customer to live e-file' : 'Move this customer back to IRS test mode'}
        description={data?.name}
        body={
          modeTarget === 'live' ? (
            <p className="text-base text-muted-foreground">
              Everything filed after this goes to the IRS for real. Confirm their TCC, enabled forms and test
              e-files first.
            </p>
          ) : (
            <p className="text-base text-muted-foreground">
              New e-files will go to the IRS test system again. E-files already sent are not affected.
            </p>
          )
        }
        confirmLabel={modeTarget === 'live' ? 'Switch to live' : 'Move to IRS test mode'}
        destructive={modeTarget === 'live'}
        requireTyped={modeTarget === 'live' ? 'LIVE' : undefined}
        pending={switchMode.isPending}
        onConfirm={() => {
          if (modeTarget) switchMode.mutate(modeTarget);
        }}
      />

      <ConfirmDialog
        open={keyToRevoke !== null}
        onOpenChange={(open) => {
          if (!open) setKeyToRevoke(null);
        }}
        title="Revoke this key"
        description={keyToRevoke ? `${keyToRevoke.name}, prefix ${keyToRevoke.prefix}` : undefined}
        body={
          <p className="text-base text-muted-foreground">
            Every request made with this key is refused from the moment you confirm. E-files already
            sent are not affected. This cannot be undone; the owner creates a new key if they need one.
          </p>
        }
        confirmLabel="Revoke key"
        destructive
        pending={revokeKey.isPending}
        onConfirm={() => {
          if (keyToRevoke) revokeKey.mutate(keyToRevoke.id);
        }}
      />
    </>
  );
}
